import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Calendar, DateObject } from 'react-multi-date-picker'
import persian from 'react-date-object/calendars/persian'
import persian_fa from 'react-date-object/locales/persian_fa'
import 'react-multi-date-picker/styles/colors/green.css'
import {
    IoCalendarOutline,
    IoCheckmark,
    IoClose,
    IoOptionsOutline,
} from 'react-icons/io5'
import CustomBottomSheet from 'components/bottomSheet'
import SortPill from 'components/sortPill'
import {
    activeFilters,
    dateToJalaliDay,
    dayRangeLabel,
    IFilterBox,
    IFundOption,
    ITransactionFilters,
    jalaliDayToDate,
    kindOption,
    kindOptionsFor,
    NO_FILTERS,
    TFilterKind,
    TFilterName,
    typedFundTitle,
} from './helpers'

const sameBox = (a: IFilterBox | null, b: IFilterBox | null) =>
    a?.type === b?.type && a?.id === b?.id

/** The button beside the search box, with how many filters are on. */
export function FilterButton({
    count,
    onClick,
}: {
    count: number
    onClick: () => void
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-haspopup="dialog"
            aria-label={
                count > 0
                    ? `فیلتر تراکنش‌ها، ${count} فیلتر فعال`
                    : 'فیلتر تراکنش‌ها'
            }
            className={classNames(
                'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] transition-colors',
                count > 0
                    ? 'bg-green-2 text-green-1'
                    : 'bg-sunken text-text-secondary'
            )}
        >
            <IoOptionsOutline aria-hidden className="h-5 w-5" />
            {count > 0 && (
                <span
                    aria-hidden
                    className="absolute -left-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-green-1 px-1 text-[10px] font-bold leading-none text-white"
                >
                    {count}
                </span>
            )}
        </button>
    )
}

export interface IFilterChip {
    name: TFilterName
    label: string
}

/** What each active filter is called, in the order they are listed. */
export function filterChips(
    filters: ITransactionFilters,
    boxTitle: string | null
): IFilterChip[] {
    return activeFilters(filters).map((name) => ({
        name,
        label:
            name === 'box'
                ? // Its name unknown, its kind at least.
                  boxTitle ??
                  (filters.box ? typedFundTitle(null, filters.box.type) : '')
                : name === 'kind'
                ? kindOption(filters.kind)?.label ?? ''
                : dayRangeLabel(filters.from, filters.to),
    }))
}

/**
 * The filters that are on, each one a chip that takes itself off. Wraps
 * rather than scrolling sideways: a fund's name can be most of a row, and a
 * chip that has scrolled out of sight is a filter nobody knows is on.
 */
export function FilterChips({
    chips,
    onClear,
}: {
    chips: IFilterChip[]
    onClear: (name: TFilterName | 'all') => void
}) {
    if (chips.length === 0) return null
    return (
        <div
            aria-label="فیلترهای فعال"
            role="group"
            className="flex flex-wrap items-center gap-1.5 px-4 pt-2.5"
        >
            {chips.map((chip) => (
                <span
                    key={chip.name}
                    className="inline-flex min-w-0 max-w-full items-center gap-0.5 rounded-full border border-green-1 bg-green-2 py-0.5 pl-0.5 pr-3 text-[11px] font-bold text-green-1"
                >
                    <span className="min-w-0 truncate">{chip.label}</span>
                    <button
                        type="button"
                        aria-label={`حذف فیلتر ${chip.label}`}
                        onClick={() => onClear(chip.name)}
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                    >
                        <IoClose aria-hidden className="h-3.5 w-3.5" />
                    </button>
                </span>
            ))}
            <button
                type="button"
                onClick={() => onClear('all')}
                className="shrink-0 px-1.5 py-1 text-[11px] font-bold text-text-secondary"
            >
                حذف همه
            </button>
        </div>
    )
}

function SectionTitle({ id, children }: { id: string; children: string }) {
    return (
        <p id={id} className="mb-2 mt-4 text-[13px] font-bold text-black">
            {children}
        </p>
    )
}

interface IFilterSheetProps {
    isOpen: boolean
    /** what is on now; the sheet starts from it every time it opens */
    filters: ITransactionFilters
    funds: IFundOption[]
    fundsLoading: boolean
    fundsError: boolean
    onRetryFunds: () => void
    /** the name of the box that is on, when it is not in `funds` */
    boxTitle: string | null
    onApply: (next: ITransactionFilters) => void
    onClear: () => void
    onDismiss: () => void
}

/**
 * «فیلتر تراکنش‌ها»: which fund, what kind of payment, which days. Nothing
 * changes on the page until «نمایش تراکنش‌ها» — three choices made one at a
 * time would otherwise be three reloads of the list under the sheet.
 */
export function FilterSheet({
    isOpen,
    filters,
    funds,
    fundsLoading,
    fundsError,
    onRetryFunds,
    boxTitle,
    onApply,
    onClear,
    onDismiss,
}: IFilterSheetProps) {
    const [draft, setDraft] = useState<ITransactionFilters>(filters)
    const [calendarOpen, setCalendarOpen] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        setDraft(filters)
        setCalendarOpen(false)
        // Only as it opens: while it is open the draft is theirs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    // Brought into view as it opens; afterwards where the sheet sits is
    // theirs. The calendar mounts empty and fills its days in a few frames
    // later, so it is followed while it grows, briefly — scrolled once, at
    // mount, it moved the sheet 27px and left the days below the buttons.
    const calendarRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const node = calendarRef.current
        if (!calendarOpen || !node) return
        const reveal = () =>
            node.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
        reveal()
        if (typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(reveal)
        observer.observe(node)
        const settled = window.setTimeout(() => observer.disconnect(), 600)
        return () => {
            observer.disconnect()
            window.clearTimeout(settled)
        }
    }, [calendarOpen])

    const chooseBox = (box: IFilterBox | null) =>
        setDraft((current) => {
            const option = kindOption(current.kind)
            // A kind the new box can never have would only ever list nothing.
            const keepsKind =
                !option || !box || option.boxTypes.includes(box.type)
            return { ...current, box, kind: keepsKind ? current.kind : null }
        })

    const chooseKind = (kind: TFilterKind | null) =>
        setDraft((current) => ({ ...current, kind }))

    const chooseDays = (dates: DateObject[] | null) => {
        const days = (dates ?? []).map((date) => dateToJalaliDay(date.toDate()))
        // One tap is one day; the second closes the range.
        const from = days[0] ?? null
        const to = days[1] ?? days[0] ?? null
        setDraft((current) => ({ ...current, from, to }))
    }

    // The chosen box may be one the member has since left: it stays listed,
    // so the sheet still says what is on.
    const chosenMissing =
        !!draft.box && !funds.some((fund) => sameBox(fund.box, draft.box))
    const fundRows: { box: IFilterBox | null; title: string }[] = [
        { box: null, title: 'همهٔ صندوق‌ها' },
        ...(chosenMissing && draft.box
            ? [
                  {
                      box: draft.box,
                      title:
                          sameBox(draft.box, filters.box) && boxTitle
                              ? boxTitle
                              : typedFundTitle(null, draft.box.type),
                  },
              ]
            : []),
        ...funds,
    ]

    const kinds = kindOptionsFor(draft.box?.type ?? null)
    const hasDays = !!draft.from || !!draft.to
    const calendarValue = [draft.from, draft.to]
        .filter((day): day is string => !!day)
        .filter((day, index, all) => all.indexOf(day) === index)
        .map(jalaliDayToDate)

    return (
        <CustomBottomSheet isOpen={isOpen} onDismiss={onDismiss} dragHandleOnly>
            <div
                role="dialog"
                aria-labelledby="transactions-filter-title"
                className="flex max-h-[80dvh] flex-col"
            >
                <div className="flex shrink-0 items-center justify-between gap-2">
                    <p
                        id="transactions-filter-title"
                        className="mb-0 text-[15px] font-bold text-black"
                    >
                        فیلتر تراکنش‌ها
                    </p>
                    <button
                        type="button"
                        aria-label="بستن"
                        onClick={onDismiss}
                        className="-ml-1 p-1 text-text-secondary"
                    >
                        <IoClose aria-hidden className="h-5 w-5" />
                    </button>
                </div>

                <div className="-mx-4 min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-2">
                    <SectionTitle id="transactions-filter-box">
                        صندوق
                    </SectionTitle>
                    {fundsError && funds.length === 0 ? (
                        <div className="mb-2 flex items-center justify-between gap-2 rounded-[10px] bg-sunken px-3 py-2.5 text-[13px]">
                            <p className="mb-0 text-text-secondary">
                                فهرست صندوق‌ها دریافت نشد
                            </p>
                            <button
                                type="button"
                                onClick={onRetryFunds}
                                className="shrink-0 font-bold text-green-1"
                            >
                                تلاش مجدد
                            </button>
                        </div>
                    ) : null}
                    <div
                        role="radiogroup"
                        aria-labelledby="transactions-filter-box"
                        className="divide-y divide-divider overflow-hidden rounded-card border border-[#E4E7EB]"
                    >
                        {fundRows.map((fund) => {
                            const checked = sameBox(fund.box, draft.box)
                            return (
                                <button
                                    key={
                                        fund.box
                                            ? `${fund.box.type}-${fund.box.id}`
                                            : 'all'
                                    }
                                    type="button"
                                    role="radio"
                                    aria-checked={checked}
                                    onClick={() => chooseBox(fund.box)}
                                    className={classNames(
                                        'flex w-full min-w-0 items-center gap-2 px-3 py-2.5 text-right text-[13px]',
                                        checked
                                            ? 'bg-green-2 font-bold text-green-1'
                                            : 'bg-white text-black'
                                    )}
                                >
                                    <span className="min-w-0 flex-1 truncate">
                                        {fund.title}
                                    </span>
                                    {checked && (
                                        <IoCheckmark
                                            aria-hidden
                                            className="h-4 w-4 shrink-0"
                                        />
                                    )}
                                </button>
                            )
                        })}
                        {fundsLoading && funds.length === 0 && (
                            <p
                                role="status"
                                className="mb-0 bg-white px-3 py-2.5 text-[13px] text-text-muted"
                            >
                                در حال دریافت صندوق‌ها…
                            </p>
                        )}
                    </div>

                    <SectionTitle id="transactions-filter-kind">
                        نوع تراکنش
                    </SectionTitle>
                    <div
                        role="radiogroup"
                        aria-labelledby="transactions-filter-kind"
                        className="flex flex-wrap gap-2"
                    >
                        <SortPill
                            checked={draft.kind === null}
                            onChange={() => chooseKind(null)}
                            label="همه"
                            aria="همهٔ انواع تراکنش"
                        />
                        {kinds.map((option) => (
                            <SortPill
                                key={option.key}
                                checked={draft.kind === option.key}
                                onChange={() => chooseKind(option.key)}
                                label={option.label}
                            />
                        ))}
                    </div>

                    <SectionTitle id="transactions-filter-date">
                        تاریخ
                    </SectionTitle>
                    <div className="flex min-w-0 items-center gap-2 rounded-[10px] bg-sunken px-3 py-2 text-[13px]">
                        <IoCalendarOutline
                            aria-hidden
                            className="h-4 w-4 shrink-0 text-text-secondary"
                        />
                        <span
                            className={classNames(
                                'min-w-0 flex-1 truncate',
                                hasDays ? 'font-bold text-black' : 'text-text-muted'
                            )}
                        >
                            {hasDays
                                ? dayRangeLabel(draft.from, draft.to)
                                : 'همهٔ روزها'}
                        </span>
                        {hasDays && (
                            <button
                                type="button"
                                onClick={() => chooseDays(null)}
                                className="shrink-0 px-1 text-[12px] font-bold text-text-secondary"
                            >
                                حذف
                            </button>
                        )}
                        <button
                            type="button"
                            aria-expanded={calendarOpen}
                            // Short on screen so the range keeps its year.
                            aria-label={calendarOpen ? 'بستن تقویم' : undefined}
                            onClick={() => setCalendarOpen((open) => !open)}
                            className="shrink-0 px-1 text-[12px] font-bold text-green-1"
                        >
                            {calendarOpen
                                ? 'بستن'
                                : hasDays
                                ? 'تغییر'
                                : 'انتخاب روز'}
                        </button>
                    </div>
                    {/* Inline, not a popup behind an input: a popup opened
                        inside a scrolling sheet is clipped by it. It opens
                        below the fold, so it is brought into view. */}
                    {calendarOpen && (
                        <div
                            ref={calendarRef}
                            className="mt-2 flex flex-col items-center"
                        >
                            <Calendar
                                className="green"
                                calendar={persian}
                                locale={persian_fa}
                                range
                                value={calendarValue}
                                onChange={(dates: DateObject[]) =>
                                    chooseDays(dates)
                                }
                                maxDate={new Date()}
                            />
                            <p className="mb-0 mt-2 text-center text-[11px] leading-5 text-text-secondary">
                                روز اول و روز آخر را بزنید؛ برای یک روز، همان
                                یک روز را.
                            </p>
                        </div>
                    )}
                </div>

                <div className="flex shrink-0 gap-2 border-t border-divider pt-3">
                    <button
                        type="button"
                        onClick={() => onApply(draft)}
                        className="flex-1 rounded-full bg-green-1 py-2.5 text-[15px] font-bold text-white transition-transform active:scale-[0.99] motion-reduce:transition-none"
                    >
                        نمایش تراکنش‌ها
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setDraft(NO_FILTERS)
                            onClear()
                        }}
                        className="flex-1 rounded-full border border-stroke bg-white py-2.5 text-[15px] font-bold text-text-secondary"
                    >
                        حذف فیلترها
                    </button>
                </div>
            </div>
        </CustomBottomSheet>
    )
}
